import type { RefObject } from "react";

export enum AI_ASST_MSG_TYPES {
  user = "user",
  assistant = "assistant",
}

export interface IChatAttachment {
  name: string;
  url: string;
}

/**
 * A file the AI rendered + uploaded for an assistant turn (from the SSE
 * `final` event's `attachments`, or persisted `generatedAttachments`).
 */
export interface IAiGeneratedAttachment {
  name: string;
  url: string;
  format: "csv" | "pdf" | (string & {});
  content_type?: string;
  size_bytes?: number;
  row_count?: number;
}

export interface ISingleChatBubble {
  id: number;
  messageType: AI_ASST_MSG_TYPES;
  content: string;
  createdAt: string;
  attachments?: IChatAttachment[];
  attachmentUrls?: string[];
  // Assistant bubbles only: AI-generated files + any preparation/upload errors.
  generatedAttachments?: IAiGeneratedAttachment[];
  attachmentErrors?: string[];
}

export interface IChatHistoryItem {
  question: ISingleChatBubble;
  answer: ISingleChatBubble;
}

enum CONVERSATION_STATUSES {
  PENDING = "pending",
  RUNNING = "running",
  COMPLETED = "completed",
  FAILED = "failed",
}

export interface IChatSessionData {
  sessionId: string;
  conversationId: number;
  title: string;
  status: CONVERSATION_STATUSES;
  history: {
    question: ISingleChatBubble;
    answer: ISingleChatBubble;
  }[];
  answer: string;
  answerId: number;
  generatedAttachments?: IAiGeneratedAttachment[];
  attachmentErrors?: string[];
  lastUserMessageId: number;
  lastAiMessageId: number;
}

export interface ISingleConversationHistoryItem {
  id: number;
  sessionId: string;
  title: string;
  status: CONVERSATION_STATUSES;
  createdAt: string;
  updatedAt: string;
  pinned: boolean;
}

export interface IQuerySingleQuestion {
  id: number;
  content: string;
  createdAt: string;
}

export type TQueriesHistory = ISingleConversationHistoryItem & {
  questions: IQuerySingleQuestion[];
};

export type Conversation_Types = "chat" | "query";

export type TPromptInputRef = RefObject<HTMLTextAreaElement | null>;

export interface IConversationHistory {
  conversationId: number;
  history: IChatHistoryItem[];
  sessionId: string;
}

/**
 * The app page the user is looking at, sent with a chat turn as `pageContext`
 * (the Maya sidebar). Mirrors the API's AiPageContextDto.
 */
export interface IAiPageContext {
  /** Pathname plus navigation params only (see PAGE_CONTEXT_PARAMS). */
  path: string;
  title?: string;
  breadcrumbs?: string[];
}
